import React, { useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import { makeStyles } from 'tss-react/mui'
import { Button } from '@mui/material'
import { ExpandMore, ExpandLess } from '@mui/icons-material'
import { ValidMaterialPricingDetailFragment } from '@src/fragments/ProductPricing.generated'
import { ViewPreference } from '@src/routes/SearchResults/SearchResults'

import ProductCard from './ProductCard'
import SkeletonCard from './SkeletonCard'
import ViewProductOptionsSelectableChips from './ViewProductOptionsSelectableChips'

const useStyles = makeStyles()((theme) => ({
  viewAllButton: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  viewAllButtonText: {
    color: theme.palette.primary.main,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: theme.typography.fontWeightMedium,
    textTransform: 'none',
    padding: theme.spacing(2),
    '& .MuiButton-endIcon': {
      marginLeft: theme.spacing(0.5),
    },
  },
}))

interface MobileProductDisplayProps {
  materialPricing: ValidMaterialPricingDetailFragment[] | undefined
  pricingLoading: boolean
  view: ViewPreference | null
  showAllOptions: boolean
  setShowAllOptions: (show: boolean) => void
  setIsBuyBoxSheetOpen: (open: boolean) => void
  mobileSelectedMaterial: ValidMaterialPricingDetailFragment | null
  setMobileSelectedMaterial: (
    material: ValidMaterialPricingDetailFragment | null
  ) => void
  handleSetMaterialPosition: (height: number, top: number) => void
}

const MobileProductDisplay: React.FC<MobileProductDisplayProps> = ({
  materialPricing,
  pricingLoading,
  view,
  showAllOptions,
  setShowAllOptions,
  setIsBuyBoxSheetOpen,
  mobileSelectedMaterial,
  setMobileSelectedMaterial,
  handleSetMaterialPosition,
}) => {
  const { classes } = useStyles()
  const chipItemsRef = useRef<HTMLDivElement | null>(null)
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])
  itemRefs.current = []
  if (pricingLoading && view === ViewPreference.list) {
    return (
      <div>
        {Array.from({ length: 3 }).map((_, index) => (
          <SkeletonCard key={index} />
        ))}
      </div>
    )
  }

  const handleMaterialSelect = (
    material: ValidMaterialPricingDetailFragment,
    selectedHeight,
    rectTop
  ) => {
    setMobileSelectedMaterial(material)
    setIsBuyBoxSheetOpen(true)
    handleSetMaterialPosition(selectedHeight, rectTop)
  }

  const displayLimit = view === ViewPreference.grid ? 6 : 3
  const displayedMaterials = showAllOptions
    ? materialPricing
    : materialPricing?.slice(0, displayLimit)

  return (
    <div>
      {view === ViewPreference.grid ? (
        <div ref={chipItemsRef}>
          <ViewProductOptionsSelectableChips
            items={
              materialPricing?.map((product) => ({
                packSize:
                  product?.packageSize?.trim() || product?.materialNumber,
                value: product?.materialNumber,
                price: product?.price ?? 0,
                currency: product?.currency,
                availabilities: product?.availabilities,
                hidePriceMessageKey: product?.hidePriceMessageKey,
              })) ?? []
            }
            selectedChip={mobileSelectedMaterial?.materialNumber ?? ''}
            loading={pricingLoading}
            loadCount={2}
            onSelect={(materialNumber) => {
              const material = materialPricing?.find(
                (product) => product.materialNumber === materialNumber
              )
              if (material) {
                const totalHeight =
                  (chipItemsRef.current?.offsetHeight || 0) +
                  (chipItemsRef.current?.offsetTop || 0)
                const rectTop =
                  (chipItemsRef.current?.getBoundingClientRect().top || 0) +
                  (chipItemsRef.current?.offsetHeight || 0)
                handleMaterialSelect(material, totalHeight, rectTop)
              }
            }}
          />
        </div>
      ) : (
        <>
          {displayedMaterials?.map((material, index) => (
            <div
              key={`${index}-${material.materialNumber}`}
              ref={(el) => {
                itemRefs.current[index] = el as HTMLDivElement
              }}
            >
              <ProductCard
                key={material.materialNumber}
                material={material}
                isSelected={
                  mobileSelectedMaterial?.materialNumber ===
                  material.materialNumber
                }
                onSelect={() => {
                  const totalHeight =
                    (itemRefs.current[index]?.offsetHeight || 0) +
                    (itemRefs.current[index]?.offsetTop || 0)
                  const rectTop =
                    (itemRefs?.current[index]?.getBoundingClientRect().top ||
                      0) + (itemRefs.current[index]?.offsetHeight || 0)
                  handleMaterialSelect(material, totalHeight, rectTop)
                }}
              />
            </div>
          ))}
          {/* View All Options / Hide Options Button */}
          {materialPricing && materialPricing.length > displayLimit && (
            <div className={classes.viewAllButton}>
              <Button
                onClick={() => {
                  const isInDisplayedMaterials = displayedMaterials?.some(
                    (material) =>
                      material.materialNumber ===
                      mobileSelectedMaterial?.materialNumber
                  )
                  if (showAllOptions || !isInDisplayedMaterials) {
                    setMobileSelectedMaterial(null)
                    setIsBuyBoxSheetOpen(false)
                  }
                  setShowAllOptions(!showAllOptions)
                }}
                className={classes.viewAllButtonText}
                endIcon={showAllOptions ? <ExpandLess /> : <ExpandMore />}
              >
                {showAllOptions ? (
                  <FormattedMessage
                    id="HIDE_OPTIONS"
                    defaultMessage="Hide Options"
                  />
                ) : (
                  <FormattedMessage
                    id="VIEW_ALL_OPTIONS"
                    defaultMessage="View All Options ({count})"
                    values={{ count: materialPricing.length }}
                  />
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default MobileProductDisplay
